import React, { useEffect } from 'react';
import AOS from 'aos';
import {
  ArrowLeft, Bike, DollarSign, MapPin, Clock, ShieldCheck, Smartphone,
  Wallet, CalendarClock, Headphones, TrendingUp, Star, CheckCircle2,
  Download, UserPlus, Power, PackageCheck, Car, Footprints,
} from 'lucide-react';
import Footer from '../../components/Footer';
import CookieConsent from '../../components/CookieConsent';
import { driverAppLinks } from '../../config/appLinks';
import { ScreenNewOrder, ScreenRoute, ScreenEarnings, HeroScooter, StepBadge, DotPattern } from './illustrations';

const PAGE_URL = 'https://leodelivery.com.br/download-app-driver';
const PAGE_TITLE = 'Leo Go | Seja Entregador Parceiro — Baixe o App de Entregas';
const PAGE_DESC = 'Baixe o Leo Go e trabalhe como entregador parceiro do Léo Delivery. Ganhos por entrega, rotas otimizadas, saque rápido e horários flexíveis. Disponível para Android e iPhone.';
const PAGE_KEYWORDS = 'seja entregador, app de entregas, trabalhar como entregador, entregador parceiro, ganhar dinheiro com entregas, app para motoboy, entrega de delivery, Leo Go, cadastro entregador delivery';

const benefits = [
  { icon: DollarSign, title: 'Ganhos por entrega', text: 'Veja o valor de cada corrida antes de aceitar. Sem surpresas, com pagamento transparente.' },
  { icon: MapPin, title: 'Rotas inteligentes', text: 'Navegação integrada com a melhor rota até a coleta e a entrega, economizando tempo e combustível.' },
  { icon: CalendarClock, title: 'Horários flexíveis', text: 'Fique online quando quiser. Você decide seus dias, horários e quanto quer trabalhar.' },
  { icon: Wallet, title: 'Saque rápido', text: 'Receba seus ganhos com agilidade e acompanhe tudo direto pelo aplicativo.' },
  { icon: ShieldCheck, title: 'Segurança e suporte', text: 'Time de apoio dedicado e recursos de segurança para você entregar com tranquilidade.' },
  { icon: TrendingUp, title: 'Mais entregas na sua região', text: 'Conectamos você aos restaurantes parceiros perto de você para reduzir tempo ocioso.' },
];

const steps = [
  { icon: Download, title: 'Baixe o Leo Go', text: 'Disponível gratuitamente na Google Play e na App Store.' },
  { icon: UserPlus, title: 'Faça seu cadastro', text: 'Envie seus documentos pelo app e aguarde a aprovação em poucos passos.' },
  { icon: Power, title: 'Fique online', text: 'Ative sua disponibilidade e comece a receber pedidos na sua região.' },
  { icon: PackageCheck, title: 'Entregue e receba', text: 'Colete, entregue e acompanhe seus ganhos em tempo real.' },
];

const requirements = [
  { icon: Bike, title: 'De bike, moto ou carro', text: 'Escolha o modal que combina com você para fazer as entregas.' },
  { icon: Smartphone, title: 'Smartphone com internet', text: 'Um celular Android ou iPhone com acesso à internet para usar o app.' },
  { icon: CheckCircle2, title: 'Documentos em dia', text: 'CNH (para moto/carro), documento do veículo e dados pessoais atualizados.' },
  { icon: Footprints, title: 'Maior de 18 anos', text: 'É necessário ser maior de idade para se cadastrar como entregador parceiro.' },
];

const faqs = [
  {
    q: 'Como faço para ser um entregador parceiro do Léo Delivery?',
    a: 'Baixe o app Leo Go na Google Play ou na App Store, faça seu cadastro enviando seus documentos e, após a aprovação, é só ficar online para começar a receber pedidos.',
  },
  {
    q: 'Preciso ter moto para entregar?',
    a: 'Não. Você pode fazer entregas de bicicleta, moto ou carro. Basta escolher o modal ao se cadastrar. Para moto e carro é necessário CNH e documento do veículo.',
  },
  {
    q: 'Quanto custa usar o aplicativo Leo Go?',
    a: 'O aplicativo é gratuito para entregadores parceiros. Você baixa, se cadastra e começa a trabalhar sem pagar mensalidade.',
  },
  {
    q: 'Como e quando recebo meus ganhos?',
    a: 'Todos os seus ganhos ficam registrados no app, com pagamento transparente por entrega, e você acompanha o saldo disponível para saque diretamente pelo Leo Go.',
  },
  {
    q: 'Tenho horário fixo para trabalhar?',
    a: 'Não. Você tem total flexibilidade: fica online quando quiser e escolhe seus próprios dias e horários.',
  },
  {
    q: 'O Leo Go está disponível para Android e iPhone?',
    a: 'Sim. O aplicativo pode ser baixado gratuitamente tanto na Google Play (Android) quanto na App Store (iPhone).',
  },
];

const stats = [
  { value: '100%', label: 'Ganhos transparentes por entrega' },
  { value: '24/7', label: 'Você escolhe quando ficar online' },
  { value: 'R$ 0', label: 'Custo para baixar e se cadastrar' },
  { value: '2 min', label: 'Para começar o cadastro no app' },
];

const GooglePlayIcon = (props) => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M3.6 2.3a1 1 0 0 0-.55.9v17.6a1 1 0 0 0 .55.9l10-9.7-10-9.7Z" opacity=".9" />
    <path d="M16.9 9 5.2 2.2 15 11.9 16.9 9Z" />
    <path d="M20.6 11.2 17.9 9.6 15.9 12l2 2.4 2.7-1.6a1 1 0 0 0 0-1.6Z" />
    <path d="M5.2 21.8 16.9 15 15 12.1 5.2 21.8Z" />
  </svg>
);

const AppleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M16.4 12.7c0-2 1.6-3 1.7-3.1-1-1.4-2.4-1.6-3-1.6-1.3-.1-2.5.7-3.1.7-.6 0-1.6-.7-2.7-.7-1.4 0-2.7.8-3.4 2-1.4 2.5-.4 6.2 1 8.2.7 1 1.5 2.1 2.5 2 1-.1 1.4-.6 2.6-.6s1.5.6 2.6.6c1.1 0 1.8-1 2.5-2 .8-1.1 1.1-2.2 1.1-2.3-.1 0-2.2-.9-2.3-3.2ZM14.3 6.5c.6-.7 1-1.6.9-2.5-.8 0-1.9.6-2.5 1.2-.5.6-1 1.5-.9 2.4.9.1 1.8-.4 2.5-1.1Z" />
  </svg>
);

const StoreButton = ({ href, icon: Icon, top, bottom, light }) => {
  const available = Boolean(href);
  const content = (
    <>
      <Icon className="driver-store-btn__icon" />
      <span className="driver-store-btn__text">
        <small>{available ? top : 'Em breve'}</small>
        <strong>{bottom}</strong>
      </span>
    </>
  );
  const cls = `driver-store-btn ${light ? 'is-light' : ''}`;
  if (!available) {
    return <span className={`${cls} is-disabled`} aria-disabled="true">{content}</span>;
  }
  return (
    <a className={cls} href={href} target="_blank" rel="noopener noreferrer">{content}</a>
  );
};

const StoreButtons = ({ light }) => (
  <div className="driver-stores">
    <StoreButton href={driverAppLinks.playStore} icon={GooglePlayIcon} top="Baixar na" bottom="Google Play" light={light} />
    <StoreButton href={driverAppLinks.appStore} icon={AppleIcon} top="Baixar na" bottom="App Store" light={light} />
  </div>
);

// SEO: título, metatags e dados estruturados injetados no <head>
const useSeo = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const setMeta = (selector, attr, key, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
      return el;
    };

    setMeta('meta[name="description"]', 'name', 'description', PAGE_DESC);
    setMeta('meta[name="keywords"]', 'name', 'keywords', PAGE_KEYWORDS);
    setMeta('meta[property="og:title"]', 'property', 'og:title', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'property', 'og:description', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'property', 'og:url', PAGE_URL);
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMeta('meta[property="twitter:title"]', 'property', 'twitter:title', PAGE_TITLE);
    setMeta('meta[property="twitter:description"]', 'property', 'twitter:description', PAGE_DESC);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', PAGE_URL);

    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'MobileApplication',
          name: 'Leo Go',
          operatingSystem: 'ANDROID, IOS',
          applicationCategory: 'BusinessApplication',
          description: PAGE_DESC,
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL' },
          url: PAGE_URL,
          publisher: { '@type': 'Organization', name: 'Léo Delivery', url: 'https://leodelivery.com.br' },
        },
        {
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Início', item: 'https://leodelivery.com.br' },
            { '@type': 'ListItem', position: 2, name: 'Seja entregador', item: PAGE_URL },
          ],
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'driver-jsonld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (prevCanonical) canonical.setAttribute('href', prevCanonical);
      const s = document.getElementById('driver-jsonld');
      if (s) s.remove();
    };
  }, []);
};

// Garante que o conteúdo apareça: o AOS deixa os blocos com opacity:0 até
// animar no scroll. Como esta página é alta e o AOS.init() global roda antes
// do layout final, recalculamos as posições e, como rede de segurança,
// revelamos tudo caso a animação não dispare.
const useReveal = () => {
  useEffect(() => {
    try { AOS.refreshHard(); } catch (e) { /* noop */ }
    const reveal = () => {
      document.querySelectorAll('.driver-download [data-aos]')
        .forEach((el) => el.classList.add('aos-animate'));
    };
    const raf = requestAnimationFrame(() => { try { AOS.refresh(); } catch (e) { /* noop */ } });
    const safety = setTimeout(reveal, 1200);
    window.addEventListener('load', reveal, { once: true });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(safety);
      window.removeEventListener('load', reveal);
    };
  }, []);
};

const DownloadDriver = () => {
  useSeo();
  useReveal();

  return (
    <>
      <section className="driver-topbar">
        <div className="container">
          <a className="driver-back-link" href="/">
            <ArrowLeft size={16} /> Voltar para o site
          </a>
        </div>
      </section>

      <main className="driver-download">
        {/* HERO */}
        <section className="driver-hero">
          <DotPattern className="driver-hero__dots" />
          <div className="container">
            <div className="driver-hero__grid">
              <div className="driver-hero__content" data-aos="fade-up">
                <span className="driver-badge"><Bike size={16} /> App do entregador parceiro</span>
                <h1>Seja entregador com o <span className="text-primary-custom">Leo Go</span></h1>
                <p>
                  Baixe o aplicativo dos entregadores parceiros do Léo Delivery,
                  receba pedidos na sua região, siga rotas otimizadas e acompanhe
                  seus ganhos em tempo real. Você no controle da sua rotina.
                </p>
                <StoreButtons light />
                <div className="driver-hero__rating">
                  <span className="driver-stars">
                    <Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" />
                    <Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" />
                    <Star size={16} fill="currentColor" />
                  </span>
                  Gratuito para entregadores • Android e iPhone
                </div>
              </div>
              <div className="driver-hero__visual" data-aos="fade-left">
                <HeroScooter />
              </div>
            </div>
          </div>
        </section>

        {/* STATS */}
        <section className="driver-stats">
          <div className="container">
            <div className="driver-stats__grid">
              {stats.map((s) => (
                <div className="driver-stat" key={s.label} data-aos="fade-up">
                  <strong>{s.value}</strong>
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* COMO FUNCIONA */}
        <section className="driver-steps" id="como-funciona">
          <div className="container">
            <header className="driver-section-head" data-aos="fade-up">
              <span className="driver-eyebrow">Comece em minutos</span>
              <h2>Como se tornar um entregador parceiro</h2>
              <p>Em quatro passos simples você já está pronto para fazer suas primeiras entregas com o Leo Go.</p>
            </header>
            <div className="driver-steps__grid">
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div className="driver-step-card" key={s.title} data-aos="fade-up" data-aos-delay={i * 100}>
                    <div className="driver-step-card__num"><StepBadge n={i + 1} /></div>
                    <div className="driver-step-card__icon"><Icon size={22} /></div>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* BENEFÍCIOS */}
        <section className="driver-benefits" id="beneficios">
          <div className="container">
            <header className="driver-section-head" data-aos="fade-up">
              <span className="driver-eyebrow">Vantagens</span>
              <h2>Por que entregar com o Leo Go?</h2>
              <p>Tudo o que você precisa para trabalhar com liberdade e transparência, em um só aplicativo.</p>
            </header>
            <div className="driver-benefits__grid">
              {benefits.map(({ icon: Icon, title, text }) => (
                <div className="driver-benefit-card" key={title} data-aos="fade-up">
                  <div className="driver-benefit-card__icon"><Icon size={24} /></div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* GALERIA DE TELAS */}
        <section className="driver-gallery">
          <div className="container">
            <header className="driver-section-head is-dark" data-aos="fade-up">
              <span className="driver-eyebrow">Conheça o app</span>
              <h2>Tudo na palma da mão</h2>
              <p>Receba pedidos, navegue até o destino e acompanhe seus ganhos — sem complicação.</p>
            </header>
            <div className="driver-gallery__grid">
              <figure data-aos="fade-up"><ScreenNewOrder /><figcaption>Aceite pedidos com valor e distância antes de sair.</figcaption></figure>
              <figure data-aos="fade-up" data-aos-delay="100"><ScreenRoute /><figcaption>Navegação com a melhor rota até coleta e entrega.</figcaption></figure>
              <figure data-aos="fade-up" data-aos-delay="200"><ScreenEarnings /><figcaption>Acompanhe seus ganhos e saldo para saque.</figcaption></figure>
            </div>
          </div>
        </section>

        {/* REQUISITOS */}
        <section className="driver-req" id="requisitos">
          <div className="container">
            <div className="driver-req__grid">
              <div className="driver-req__intro" data-aos="fade-right">
                <span className="driver-eyebrow">Quem pode participar</span>
                <h2>O que você precisa para começar</h2>
                <p>
                  Ser entregador parceiro do Léo Delivery é simples. Reúna o essencial,
                  faça seu cadastro pelo Leo Go e comece a receber pedidos assim que for aprovado.
                </p>
                <div className="driver-req__modals">
                  <span><Bike size={18} /> Bike</span>
                  <span><Car size={18} /> Moto</span>
                  <span><Car size={18} /> Carro</span>
                </div>
              </div>
              <div className="driver-req__cards">
                {requirements.map(({ icon: Icon, title, text }) => (
                  <div className="driver-req-card" key={title} data-aos="fade-up">
                    <div className="driver-req-card__icon"><Icon size={22} /></div>
                    <div>
                      <h3>{title}</h3>
                      <p>{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="driver-faq" id="faq">
          <div className="container">
            <header className="driver-section-head" data-aos="fade-up">
              <span className="driver-eyebrow">Dúvidas frequentes</span>
              <h2>Perguntas sobre ser entregador</h2>
            </header>
            <div className="driver-faq__list">
              {faqs.map((f, i) => (
                <details className="driver-faq__item" key={f.q} data-aos="fade-up" open={i === 0}>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="driver-cta">
          <div className="container">
            <div className="driver-cta__box" data-aos="zoom-in">
              <Headphones size={40} className="driver-cta__ico" />
              <h2>Pronto para começar a entregar?</h2>
              <p>Baixe o Leo Go agora, faça seu cadastro e transforme seu tempo em ganhos.</p>
              <StoreButtons />
              <span className="driver-cta__note"><Clock size={15} /> Leva menos de 2 minutos para iniciar o cadastro.</span>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieConsent />
    </>
  );
};

export default DownloadDriver;
